import React, { useEffect, useRef } from "react";
import { intlShape } from "@ai360/core/intl";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

import { FieldAPI, ReportAPI } from "@ai360/core";

import {
    Checkbox,
    DateInput,
    DialogBox,
    DialogBoxFooterType,
    NoLink,
    Section,
    SelectInput,
    SubSection,
} from "~/core";
import { IOption, ReportOptionsComponent, ReportOptionsComponentProps } from ".";
import { IRecordChildObject, IRecordObject } from "~/core/components/tree-view/tree-view";

import { selectors as picklistSelectors, picklistNames, ACTIVE_YN } from "~/core/picklist";
import { getSelectedRecGuidSet } from "~/action-panel/components/rec-module/components/rec-list/selectors";
import { getRecGeneralGuidToRecSummaryMap } from "~/recs-events/recs/selectors";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { messages } from "~/reports/i18n-messages";
import { actions, model } from "~/reports/data";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import {
    getReportDataByReportType,
    getYieldByCompareByOptions,
    useReportCropOptions,
} from "~/reports/data/selectors";
import { IS_SELECTABLE } from "~/admin/setup/customer/use-product-list";

import { getBasemapOptions, getDisplayTypeOptions } from "../utils";
import { updateYieldByCompareByOptions } from "../data/actions";
import { CategoryTree } from "../components";
import { keywords } from "../keywords";

type SkyYieldByProps = {
    selectedCrop: FieldAPI.ICrop | null;
};

function SkyYieldBy({
    hideDefaultOptions,
    onInputChange,
    selectedCrop,
}: ReportOptionsComponentProps & SkyYieldByProps) {
    const { formatMessage } = useIntl();
    const suffixConst = "~";
    const prefixConst = "Yield By Management Area - ";
    const dispatch = useAppDispatch();
    const [showPageSelection, setShowPageSelection] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    const [selectedCategories, setSelectedCategories] = React.useState<Record<string, any>>();
    const reportData = useAppSelector(getReportDataByReportType(SkyYieldBy.reportName));

    const yieldByOptionsSelected = React.useRef<any>({});
    const managementAreaOptions = React.useRef<string[]>([]);

    const yieldByCompareOptions = useAppSelector(getYieldByCompareByOptions);

    const validEventTypes = [
        keywords.applicationEvent,
        keywords.plantingEvent,
        keywords.harvestEvent,
        keywords.tillageEvent,
    ];
    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);

    const validRecTypes = [keywords.applicationManual, keywords.plantingManual];
    const selectedRecGuidList = useAppSelector(getSelectedRecGuidSet);

    const crops = useReportCropOptions();
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const agRecSummaryMap = useAppSelector(getRecGeneralGuidToRecSummaryMap);
    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );
    const seasonsToDisplay = useRef<string[]>();

    const basemapOptions = useAppSelector(getBasemapOptions);
    const optionsDisabled = selectedEventGuidList.size > 0 || selectedRecGuidList.size > 0;
    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        selectedEventGuidList.size > 0 ||
        selectedRecGuidList.size > 0;
    const displayTypeOptions: IOption[] = getDisplayTypeOptions(formatMessage);

    const yieldByDataset = useRef<IRecordObject[]>([]);
    const yieldByDatasetBase: IRecordObject[] = [
        {
            name: formatMessage(messages.yieldByPlanting),
            id: keywords.planting,
            isOpen: true,
            children: [
                {
                    id: keywords.varietyHybrid,
                    name: formatMessage(messages.varietyHybrid),
                },
                {
                    id: keywords.seedingRate,
                    name: formatMessage(messages.seedingRate),
                },
                {
                    id: keywords.seedingTargetRate,
                    name: formatMessage(messages.targetRate),
                },
                {
                    id: keywords.monitorVariety,
                    name: formatMessage(messages.monitorVariety),
                },
                {
                    id: keywords.aliasVariety,
                    name: formatMessage(messages.alias),
                },
            ] as IRecordChildObject[],
        },
        {
            name: formatMessage(messages.yieldByApplication),
            id: keywords.application,
            isOpen: true,
            children: [
                {
                    id: keywords.product,
                    name: formatMessage(messages.product),
                },
                {
                    id: keywords.productAppliedRate,
                    name: formatMessage(messages.appliedRate),
                },
                {
                    id: keywords.productTargetRate,
                    name: formatMessage(messages.targetRate),
                },
            ],
        },
        {
            name: formatMessage(messages.yieldByTillage),
            id: keywords.tillage,
            isOpen: true,
            children: [
                {
                    id: keywords.tillageMethod,
                    name: formatMessage(messages.tillageMethod),
                },
                {
                    id: keywords.tillageDepth,
                    name: formatMessage(messages.tillageDepth),
                },
            ],
        },
        {
            name: formatMessage(messages.yieldBySoilType),
            id: keywords.soilType,
            isOpen: true,
            children: [
                {
                    id: keywords.soilTypeName,
                    name: formatMessage(messages.soilTypeName),
                },
            ],
        },
    ];

    useEffect(() => {
        dispatch(actions.getYieldByCompareByOption({}));
    }, []);

    useEffect(() => {
        if (Object.keys(yieldByCompareOptions).length === 0) {
            return;
        }
        if (yieldByCompareOptions.template?.length === 0) {
            return;
        }

        const _objectWithoutProperties = (obj, keys) => {
            const target = {};
            for (const i in obj) {
                if (keys.indexOf(i) >= 0) continue;
                if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
                target[i] = obj[i];
            }
            return target;
        };
        yieldByOptionsSelected.current = _objectWithoutProperties(yieldByCompareOptions, [
            "template",
        ]);
        const keysForManagementArea = [];
        const maOptions = yieldByCompareOptions.template
            ? yieldByCompareOptions.template?.map((option) => {
                  const templateName = option.templateName + suffixConst;
                  yieldByOptionsSelected.current[templateName] = option.checked;
                  keysForManagementArea.push(templateName);
                  return {
                      name: prefixConst + option.templateName,
                      id: templateName,
                      isOpen: true,
                      children: option.attributes.map((attribute) => {
                          const attributeName = templateName + attribute.attributeName;
                          yieldByOptionsSelected.current[attributeName] = attribute.checked;
                          keysForManagementArea.push(attributeName);
                          return {
                              id: attributeName,
                              name: attribute.attributeName,
                          } as IRecordChildObject;
                      }),
                  };
              })
            : [];
        setSelectedCategories(yieldByOptionsSelected.current);

        managementAreaOptions.current = keysForManagementArea;
        yieldByDataset.current = [];

        yieldByDataset.current.push(...yieldByDatasetBase, ...maOptions);
        setIsLoading(false);
    }, [yieldByCompareOptions]);

    useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);
        onInputChange(model.PROPS_SELECTED_REC_GUID_LIST, [...selectedRecGuidList]);

        if (hideDefaultOptions) {
            return;
        }
        if (selectedEventGuidList.size > 0 || selectedRecGuidList.size > 0) {
            const seasonGuids = [];
            for (const recGeneralGuid of selectedRecGuidList) {
                const rec = agRecSummaryMap.get(recGeneralGuid);
                if (validRecTypes.includes(rec.recType)) {
                    seasonGuids.push(rec.croppingSeasonGuid);
                }
            }
            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (validEventTypes.includes(event.agEventTypeName)) {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }
            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }
            seasonGuids.sort();
            seasonsToDisplay.current = seasonsText;
        } else {
            seasonsToDisplay.current = [];
        }
    }, [selectedEventGuidList, selectedRecGuidList]);

    const updateOptions = () => {
        let maString = "";
        const allCategories = { ...selectedCategories };
        managementAreaOptions.current.forEach((option) => {
            if (selectedCategories[option]) {
                maString += option + ",";
            }
            delete allCategories[option];
        });
        dispatch(
            updateYieldByCompareByOptions({ ...allCategories, managementAreaOptions: maString })
        );
        if (Object.keys(selectedCategories).some((key) => selectedCategories[key])) {
            setShowPageSelection(false);
        } else {
            setShowPageSelection(true);
        }
    };

    return (
        <div className="report-options">
            <Section testId="sky-yield-by-options-section">
                {hideDefaultOptions ? null : (
                    <React.Fragment>
                        <SubSection testId="sky-yield-by-season-and-crop-subsection">
                            <CroppingSeason
                                disabled={croppingSeasonDisabled}
                                required={!croppingSeasonDisabled}
                                reportType={SkyYieldBy.reportName}
                                valuesToDisplay={seasonsToDisplay.current}
                            />
                            <SelectInput<FieldAPI.ICrop>
                                disabled={optionsDisabled}
                                optionIsHiddenKey={IS_SELECTABLE}
                                placeholderText={formatMessage(messages.crop)}
                                options={optionsDisabled ? [] : crops}
                                onChange={(value) => {
                                    onInputChange(
                                        model.PROPS_CROP_GUID,
                                        value ? value.cropGuid : null
                                    );
                                }}
                                value={optionsDisabled ? null : (selectedCrop as FieldAPI.ICrop)}
                                testId="sky-yield-by-crop-select"
                            />
                        </SubSection>
                        <SubSection testId="sky-yield-by-dates-subsection">
                            <DateInput
                                disabled={optionsDisabled}
                                placeholderText={formatMessage(messages.startDate)}
                                onChange={(value) => onInputChange(model.PROPS_START_DATE, value)}
                                timeFormat={false}
                                value={reportData[model.PROPS_START_DATE]}
                                testId="sky-yield-by-start-date-date"
                            />
                            <DateInput
                                disabled={optionsDisabled}
                                placeholderText={formatMessage(messages.endDate)}
                                onChange={(value) => onInputChange(model.PROPS_END_DATE, value)}
                                timeFormat={false}
                                value={reportData[model.PROPS_END_DATE]}
                                testId="sky-yield-by-end-date-date"
                            />
                        </SubSection>
                    </React.Fragment>
                )}
                <SubSection testId="sky-yield-by-display-type-subsection">
                    <SelectInput
                        required
                        clearable={false}
                        containerClassNames={["display-options-list"]}
                        placeholderText={formatMessage(messages.displayType)}
                        options={displayTypeOptions}
                        onChange={(stringValue) => {
                            const value = stringValue !== "coverage";
                            onInputChange(model.PROPS_SHOW_YIELD_POLYGON, value);
                        }}
                        value={reportData[model.PROPS_SHOW_YIELD_POLYGON] ? "polygon" : "coverage"}
                        testId="sky-yield-by-display-type-select"
                    />
                    <SelectInput
                        required
                        optionIsHiddenKey={ACTIVE_YN}
                        containerClassNames={["yield-basemap-select"]}
                        clearable={false}
                        placeholderText={formatMessage(messages.selectedBasemap)}
                        options={basemapOptions}
                        onChange={(value) => onInputChange(model.PROPS_SELECTED_BASEMAP, value)}
                        value={reportData[model.PROPS_SELECTED_BASEMAP]}
                        testId="sky-yield-by-basemap-select"
                    />
                </SubSection>
                <SubSection testId="sky-yield-by-compare-by-subsection">
                    <NoLink
                        className="categories-link report-options-modal-link page-selection"
                        label={formatMessage(messages.yieldByCompareByOptions)}
                        onClick={() =>
                            yieldByDataset.current.length > 0 ? setShowPageSelection(true) : null
                        }
                        disabled={isLoading}
                    />
                    <DialogBox
                        unrestricted
                        title={formatMessage(messages.yieldByCompareByOptions)}
                        isOpen={showPageSelection}
                        onAction={() => updateOptions()}
                        onClose={() => setShowPageSelection(false)}
                        footerType={DialogBoxFooterType.ACTION_CANCEL}
                        testId="sky-yield-by-compare-by-modal"
                    >
                        <CategoryTree
                            onStatusChange={(values) => setSelectedCategories(values)}
                            formatMessage={formatMessage}
                            categories={yieldByOptionsSelected.current}
                            categoryDataSet={yieldByDataset.current}
                        />
                    </DialogBox>
                    <Checkbox
                        containerClassNames={["yield-custom-imagery"]}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_USE_CUSTOM_IMAGERY, value)
                        }
                        value={reportData[model.PROPS_USE_CUSTOM_IMAGERY]}
                        label={formatMessage(messages.useCustomImagery)}
                        testId="sky-yield-by-use-custom-imagery-checkbox"
                    />
                </SubSection>
            </Section>
        </div>
    );
}

SkyYieldBy.propTypes = {
    intl: intlShape,
    reportPreferences: PropTypes.object,
    selectedCrop: null,
};

SkyYieldBy.defaultReportOptions = {
    [model.PROPS_USE_CUSTOM_IMAGERY]: false,
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_CROPPING_SEASON_GUID]: [],
    [model.PROPS_CROP_GUID]: null,
    [model.PROPS_START_DATE]: null,
    [model.PROPS_END_DATE]: null,
    [model.PROPS_SHOW_YIELD_POLYGON]: false,
    [model.PROPS_SELECTED_BASEMAP]: "reportImagery",
};

SkyYieldBy.reportPreferences = [model.PROPS_SELECTED_BASEMAP, model.PROPS_SHOW_YIELD_POLYGON];

SkyYieldBy.reportName = ReportAPI.ReportNames.AGVANCE_SKY_YIELD_BY_REPORT;

export default SkyYieldBy as ReportOptionsComponent<ReportOptionsComponentProps & SkyYieldByProps>;
